import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { Alert, Box, CircularProgress, Paper, Typography } from '@mui/material';
import { AppRole } from '../types';
import { hasRequiredRole } from '../config/permissions';

interface ProtectedRouteProps {
  children: JSX.Element;
  allowedRoles?: AppRole[];
}

const ProtectedRoute = ({ children, allowedRoles }: ProtectedRouteProps) => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (!hasRequiredRole(user?.role, allowedRoles)) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 3,
        }}
      >
        <Paper sx={{ p: 4, maxWidth: 520, width: '100%' }}>
          <Typography variant="h5" gutterBottom>
            Access restricted
          </Typography>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Your current role does not have access to this route.
          </Alert>
          <Typography color="text.secondary">
            If you need access, ask a Super Admin to update your permissions.
          </Typography>
        </Paper>
      </Box>
    );
  }

  return children;
};

export default ProtectedRoute;
